'use no memo'

import {Editor} from '@tiptap/core'
import {useEditorState} from '@tiptap/react'
import clsx from 'clsx'
import {FC, ReactNode, useEffect, useRef, useState} from 'react'
import {toast} from 'react-toastify'

import {pickImageFile, uploadRichTextImage} from './uploadImage'

type ToolbarProps = {
  editor: Editor
  acceptImage: boolean
  codeView: boolean
  onToggleCodeView: () => void
  fullscreen: boolean
  onToggleFullscreen: () => void
}

const FONT_FAMILIES = [
  ['Arial, Helvetica, sans-serif', 'Arial'],
  ['Georgia, serif', 'Georgia'],
  ['Times New Roman, Times, serif', 'Times New Roman'],
  ['Courier New, Courier, monospace', 'Courier New'],
  ['Verdana, Geneva, sans-serif', 'Verdana'],
  ['Tahoma, Geneva, sans-serif', 'Tahoma'],
] as const

const FONT_SIZES = ['8', '9', '10', '11', '12', '14', '16', '18', '20', '24', '36'] as const

const PALETTE = [
  '#000000',
  '#424242',
  '#636363',
  '#9c9c94',
  '#cec6ce',
  '#efefef',
  '#ffffff',
  '#ff0000',
  '#ff9c00',
  '#ffff00',
  '#00ff00',
  '#00ffff',
  '#0000ff',
  '#9c00ff',
  '#ff00ff',
  '#f7c6ce',
  '#ffe7ce',
  '#ffefc6',
  '#d6efd6',
  '#cedee7',
  '#cee7f7',
  '#d6c6e7',
  '#e7c6d6',
  '#e79c9c',
  '#ffc69c',
  '#ffe79c',
  '#b5d6a5',
  '#a5c6ce',
  '#9cc6ef',
  '#b5a5d6',
  '#d6a5bd',
]

const ToolbarButton: FC<{
  onClick: () => void
  active?: boolean
  disabled?: boolean
  title: string
  children: ReactNode
}> = ({onClick, active = false, disabled = false, title, children}) => (
  <button
    type='button'
    tabIndex={-1}
    className={clsx('rte-btn', {active})}
    disabled={disabled}
    title={title}
    aria-label={title}
    aria-pressed={active}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
  >
    {children}
  </button>
)

/** Small self-managed dropdown (react-bootstrap Dropdown steals editor focus). */
const ToolbarDropdown: FC<{
  title: string
  icon: ReactNode
  active?: boolean
  children: (close: () => void) => ReactNode
}> = ({title, icon, active, children}) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div className='rte-dropdown' ref={ref}>
      <button
        type='button'
        tabIndex={-1}
        className={clsx('rte-btn', {active: active || open})}
        title={title}
        aria-label={title}
        aria-expanded={open}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen((v) => !v)}
      >
        {icon}
        <i className='bi bi-caret-down-fill rte-caret'></i>
      </button>
      {open && <div className='rte-dropdown-menu'>{children(() => setOpen(false))}</div>}
    </div>
  )
}

const ColorGrid: FC<{label: string; onPick: (color: string | null) => void}> = ({
  label,
  onPick,
}) => (
  <div className='rte-color-menu'>
    <div className='rte-menu-label'>{label}</div>
    <div className='rte-color-grid'>
      {PALETTE.map((color) => (
        <button
          key={color}
          type='button'
          className='rte-color-swatch'
          style={{backgroundColor: color}}
          title={color}
          aria-label={color}
          onClick={() => onPick(color)}
        />
      ))}
    </div>
    <div className='d-flex align-items-center gap-2 mt-3'>
      <input
        type='color'
        className='form-control form-control-color form-control-sm'
        title='Custom color'
        onChange={(e) => onPick(e.target.value)}
      />
      <button
        type='button'
        className='btn btn-sm btn-light flex-grow-1'
        onClick={() => onPick(null)}
      >
        Remove color
      </button>
    </div>
  </div>
)

/** Prefixes https:// when the user types a bare domain like example.com. */
const withScheme = (url: string) =>
  /^(https?:\/\/|mailto:|tel:)/i.test(url) ? url : `https://${url}`

const UrlForm: FC<{
  label: string
  placeholder: string
  hint?: string
  initial?: string
  submitText: string
  onSubmit: (url: string) => void
  onRemove?: () => void
}> = ({label, placeholder, hint, initial = '', submitText, onSubmit, onRemove}) => {
  const [url, setUrl] = useState(initial)
  const submit = () => {
    if (url.trim()) onSubmit(withScheme(url.trim()))
  }
  return (
    <div className='rte-url-form'>
      <div className='rte-menu-label'>{label}</div>
      <input
        type='text'
        className='form-control form-control-sm'
        placeholder={placeholder}
        value={url}
        autoFocus
        onChange={(e) => setUrl(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            submit()
          }
        }}
      />
      {hint && <p className='rte-hint'>{hint}</p>}
      <div className='d-flex gap-2 mt-2'>
        <button
          type='button'
          className='btn btn-sm btn-primary'
          disabled={!url.trim()}
          onClick={submit}
        >
          {submitText}
        </button>
        {onRemove && (
          <button type='button' className='btn btn-sm btn-light' onClick={onRemove}>
            Remove link
          </button>
        )}
      </div>
    </div>
  )
}

export const RichTextToolbar: FC<ToolbarProps> = ({
  editor,
  acceptImage,
  codeView,
  onToggleCodeView,
  fullscreen,
  onToggleFullscreen,
}) => {
  const state = useEditorState({
    editor,
    selector: ({editor: e}) => ({
      heading: e.isActive('heading') ? e.getAttributes('heading').level : 0,
      bold: e.isActive('bold'),
      italic: e.isActive('italic'),
      underline: e.isActive('underline'),
      strike: e.isActive('strike'),
      subscript: e.isActive('subscript'),
      superscript: e.isActive('superscript'),
      blockquote: e.isActive('blockquote'),
      bulletList: e.isActive('bulletList'),
      orderedList: e.isActive('orderedList'),
      link: e.isActive('link'),
      linkHref: e.getAttributes('link').href ?? '',
      table: e.isActive('table'),
      alignLeft: e.isActive({textAlign: 'left'}),
      alignCenter: e.isActive({textAlign: 'center'}),
      alignRight: e.isActive({textAlign: 'right'}),
      alignJustify: e.isActive({textAlign: 'justify'}),
      fontFamily: e.getAttributes('textStyle').fontFamily ?? '',
      fontSize: (e.getAttributes('textStyle').fontSize ?? '').replace('px', ''),
      rtl: e.isActive({dir: 'rtl'}),
      canUndo: e.can().undo(),
      canRedo: e.can().redo(),
    }),
  })

  const chain = () => editor.chain().focus()

  const insertImage = async () => {
    const file = await pickImageFile()
    if (!file) return
    try {
      const url = await uploadRichTextImage(file)
      chain().setImage({src: url}).run()
    } catch {
      // uploadRichTextImage already toasts the failure
    }
  }

  const disabled = codeView

  return (
    <div className='rte-toolbar' role='toolbar'>
      <div className='rte-group'>
        <ToolbarButton
          title='Undo (Ctrl+Z)'
          disabled={disabled || !state.canUndo}
          onClick={() => chain().undo().run()}
        >
          <i className='bi bi-arrow-counterclockwise'></i>
        </ToolbarButton>
        <ToolbarButton
          title='Redo (Ctrl+Y)'
          disabled={disabled || !state.canRedo}
          onClick={() => chain().redo().run()}
        >
          <i className='bi bi-arrow-clockwise'></i>
        </ToolbarButton>
      </div>

      <div className='rte-group'>
        <select
          className='rte-select'
          title='Paragraph format'
          disabled={disabled}
          value={state.heading}
          onChange={(e) => {
            const level = Number(e.target.value)
            if (level === 0) chain().setParagraph().run()
            else
              chain()
                .setHeading({level: level as 1 | 2 | 3 | 4 | 5 | 6})
                .run()
          }}
        >
          <option value={0}>Normal</option>
          {[1, 2, 3, 4, 5, 6].map((level) => (
            <option key={level} value={level}>
              Heading {level}
            </option>
          ))}
        </select>
        <select
          className='rte-select'
          title='Font family'
          disabled={disabled}
          value={state.fontFamily}
          onChange={(e) => {
            const value = e.target.value
            if (value) chain().setFontFamily(value).run()
            else chain().unsetFontFamily().run()
          }}
        >
          <option value=''>Default font</option>
          {FONT_FAMILIES.map(([value, name]) => (
            <option key={name} value={value} style={{fontFamily: value}}>
              {name}
            </option>
          ))}
        </select>
        <select
          className='rte-select rte-select-size'
          title='Font size'
          disabled={disabled}
          value={state.fontSize}
          onChange={(e) => {
            const value = e.target.value
            if (value) chain().setFontSize(`${value}px`).run()
            else chain().unsetFontSize().run()
          }}
        >
          <option value=''>Size</option>
          {FONT_SIZES.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </div>

      <div className='rte-group'>
        <ToolbarButton
          title='Bold (Ctrl+B)'
          active={state.bold}
          disabled={disabled}
          onClick={() => chain().toggleBold().run()}
        >
          <i className='bi bi-type-bold'></i>
        </ToolbarButton>
        <ToolbarButton
          title='Italic (Ctrl+I)'
          active={state.italic}
          disabled={disabled}
          onClick={() => chain().toggleItalic().run()}
        >
          <i className='bi bi-type-italic'></i>
        </ToolbarButton>
        <ToolbarButton
          title='Underline (Ctrl+U)'
          active={state.underline}
          disabled={disabled}
          onClick={() => chain().toggleUnderline().run()}
        >
          <i className='bi bi-type-underline'></i>
        </ToolbarButton>
        <ToolbarButton
          title='Strikethrough'
          active={state.strike}
          disabled={disabled}
          onClick={() => chain().toggleStrike().run()}
        >
          <i className='bi bi-type-strikethrough'></i>
        </ToolbarButton>
        <ToolbarButton
          title='Subscript'
          active={state.subscript}
          disabled={disabled}
          onClick={() => chain().toggleSubscript().run()}
        >
          <i className='bi bi-subscript'></i>
        </ToolbarButton>
        <ToolbarButton
          title='Superscript'
          active={state.superscript}
          disabled={disabled}
          onClick={() => chain().toggleSuperscript().run()}
        >
          <i className='bi bi-superscript'></i>
        </ToolbarButton>
        <ToolbarButton
          title='Blockquote'
          active={state.blockquote}
          disabled={disabled}
          onClick={() => chain().toggleBlockquote().run()}
        >
          <i className='bi bi-blockquote-left'></i>
        </ToolbarButton>
      </div>

      <div className='rte-group'>
        <ToolbarDropdown title='Text color' icon={<i className='bi bi-fonts'></i>}>
          {(close) => (
            <ColorGrid
              label='Text color'
              onPick={(color) => {
                if (color) chain().setColor(color).run()
                else chain().unsetColor().run()
                close()
              }}
            />
          )}
        </ToolbarDropdown>
        <ToolbarDropdown title='Highlight color' icon={<i className='bi bi-paint-bucket'></i>}>
          {(close) => (
            <ColorGrid
              label='Highlight color'
              onPick={(color) => {
                if (color) chain().setBackgroundColor(color).run()
                else chain().unsetBackgroundColor().run()
                close()
              }}
            />
          )}
        </ToolbarDropdown>
      </div>

      <div className='rte-group'>
        <ToolbarButton
          title='Bullet list'
          active={state.bulletList}
          disabled={disabled}
          onClick={() => chain().toggleBulletList().run()}
        >
          <i className='bi bi-list-ul'></i>
        </ToolbarButton>
        <ToolbarButton
          title='Numbered list'
          active={state.orderedList}
          disabled={disabled}
          onClick={() => chain().toggleOrderedList().run()}
        >
          <i className='bi bi-list-ol'></i>
        </ToolbarButton>
        <ToolbarButton
          title='Decrease indent'
          disabled={disabled}
          onClick={() => chain().outdent().run()}
        >
          <i className='bi bi-text-indent-right'></i>
        </ToolbarButton>
        <ToolbarButton
          title='Increase indent'
          disabled={disabled}
          onClick={() => chain().indent().run()}
        >
          <i className='bi bi-text-indent-left'></i>
        </ToolbarButton>
      </div>

      <div className='rte-group'>
        <ToolbarButton
          title='Align left'
          active={state.alignLeft}
          disabled={disabled}
          onClick={() => chain().setTextAlign('left').run()}
        >
          <i className='bi bi-text-left'></i>
        </ToolbarButton>
        <ToolbarButton
          title='Align center'
          active={state.alignCenter}
          disabled={disabled}
          onClick={() => chain().setTextAlign('center').run()}
        >
          <i className='bi bi-text-center'></i>
        </ToolbarButton>
        <ToolbarButton
          title='Align right'
          active={state.alignRight}
          disabled={disabled}
          onClick={() => chain().setTextAlign('right').run()}
        >
          <i className='bi bi-text-right'></i>
        </ToolbarButton>
        <ToolbarButton
          title='Justify'
          active={state.alignJustify}
          disabled={disabled}
          onClick={() => chain().setTextAlign('justify').run()}
        >
          <i className='bi bi-justify'></i>
        </ToolbarButton>
        <ToolbarButton
          title={state.rtl ? 'Left to right' : 'Right to left'}
          active={state.rtl}
          disabled={disabled}
          onClick={() =>
            chain()
              .setTextDirection(state.rtl ? null : 'rtl')
              .run()
          }
        >
          <i className={clsx('bi', state.rtl ? 'bi-text-paragraph' : 'bi-paragraph')}></i>
        </ToolbarButton>
      </div>

      <div className='rte-group'>
        <ToolbarDropdown
          title='Insert link'
          active={state.link}
          icon={<i className='bi bi-link-45deg'></i>}
        >
          {(close) => (
            <UrlForm
              label={state.link ? 'Edit link' : 'Insert link'}
              placeholder='example.com or full URL'
              initial={state.linkHref}
              submitText={state.link ? 'Save link' : 'Add link'}
              onSubmit={(url) => {
                chain().extendMarkRange('link').setLink({href: url}).run()
                close()
              }}
              onRemove={
                state.link
                  ? () => {
                      chain().extendMarkRange('link').unsetLink().run()
                      close()
                    }
                  : undefined
              }
            />
          )}
        </ToolbarDropdown>
        {acceptImage && (
          <ToolbarButton
            title='Insert image (JPG, PNG or GIF)'
            disabled={disabled}
            onClick={insertImage}
          >
            <i className='bi bi-image'></i>
          </ToolbarButton>
        )}
        <ToolbarDropdown title='Insert video' icon={<i className='bi bi-camera-video'></i>}>
          {(close) => (
            <UrlForm
              label='Insert video'
              placeholder='Paste a video link'
              hint='YouTube, Vimeo and Dailymotion links are supported.'
              submitText='Add video'
              onSubmit={(url) => {
                const inserted = chain().setVideoEmbed({src: url}).run()
                if (!inserted) toast.error('Could not embed this video URL')
                close()
              }}
            />
          )}
        </ToolbarDropdown>
        <ToolbarButton
          title='Horizontal rule'
          disabled={disabled}
          onClick={() => chain().setHorizontalRule().run()}
        >
          <i className='bi bi-hr'></i>
        </ToolbarButton>
        <ToolbarDropdown title='Table' active={state.table} icon={<i className='bi bi-table'></i>}>
          {(close) => (
            <div className='rte-table-menu'>
              <button
                type='button'
                className='dropdown-item'
                onClick={() => {
                  chain().insertTable({rows: 3, cols: 3, withHeaderRow: true}).run()
                  close()
                }}
              >
                Insert table (3×3)
              </button>
              {state.table && (
                <>
                  <div className='dropdown-divider'></div>
                  <button
                    type='button'
                    className='dropdown-item'
                    onClick={() => chain().addRowAfter().run()}
                  >
                    Add row below
                  </button>
                  <button
                    type='button'
                    className='dropdown-item'
                    onClick={() => chain().addColumnAfter().run()}
                  >
                    Add column right
                  </button>
                  <button
                    type='button'
                    className='dropdown-item'
                    onClick={() => chain().deleteRow().run()}
                  >
                    Delete row
                  </button>
                  <button
                    type='button'
                    className='dropdown-item'
                    onClick={() => chain().deleteColumn().run()}
                  >
                    Delete column
                  </button>
                  <div className='dropdown-divider'></div>
                  <button
                    type='button'
                    className='dropdown-item text-danger'
                    onClick={() => {
                      chain().deleteTable().run()
                      close()
                    }}
                  >
                    Delete table
                  </button>
                </>
              )}
            </div>
          )}
        </ToolbarDropdown>
      </div>

      <div className='rte-group'>
        <ToolbarButton
          title='Clear formatting'
          disabled={disabled}
          onClick={() => chain().unsetAllMarks().clearNodes().run()}
        >
          <i className='bi bi-eraser'></i>
        </ToolbarButton>
        <ToolbarButton title='Code view' active={codeView} onClick={onToggleCodeView}>
          <i className='bi bi-code-slash'></i>
        </ToolbarButton>
        <ToolbarButton
          title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          active={fullscreen}
          onClick={onToggleFullscreen}
        >
          <i className={clsx('bi', fullscreen ? 'bi-fullscreen-exit' : 'bi-arrows-fullscreen')}></i>
        </ToolbarButton>
      </div>
    </div>
  )
}
